// Copyright (c) Laserfiche.
// Licensed under the MIT License. See LICENSE in the project root for license information.

import { ChangeDetectorRef, Component, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { LfFieldContainerService } from './lf-field-container.service';
import { LfFieldAdhocContainerComponent } from '../lf-field-adhoc-container/lf-field-adhoc-container.component';
import { LfFieldTemplateContainerComponent } from '../lf-field-template-container/lf-field-template-container.component';
import { FieldValue, LfFieldInfo, TemplateValue } from '../field-components/utils/lf-field-types';
import { AppLocalizationService } from '@laserfiche/lf-ui-components/internal-shared';
import { Observable, of } from 'rxjs';
import { CoreUtils } from '@laserfiche/lf-js-utils';

@Component({
  selector: 'lf-field-container',
  templateUrl: './lf-field-container.component.html',
  styleUrls: ['./lf-field-container.component.css'],
  standalone: true,
  imports: [CommonModule, MatExpansionModule, LfFieldTemplateContainerComponent, LfFieldAdhocContainerComponent],
})
export class LfFieldContainerComponent {
  /**@internal */
  private ref = inject(ChangeDetectorRef);
  /**@internal */
  localizationService = inject(AppLocalizationService);

  @Input() collapsible: boolean = false;
  @Input() start_collapsed: boolean = false;

  @Output() fieldValuesChanged = new EventEmitter<boolean>();
  @Output() templateSelectedChanged = new EventEmitter<number>();
  @Output() dialogOpened = new EventEmitter<void>();
  @Output() dialogClosed = new EventEmitter<void>();

  /** @internal */
  @ViewChild(LfFieldTemplateContainerComponent) templateContainer!: LfFieldTemplateContainerComponent;
  /** @internal */
  @ViewChild(LfFieldAdhocContainerComponent) adhocContainer!: LfFieldAdhocContainerComponent;

  /** @internal */
  private lfFieldContainerService!: LfFieldContainerService;

  /** @internal */
  panelOpenState: boolean = this.start_collapsed;
  /** @internal */
  readonly FIELDS = this.localizationService.getStringLaserficheObservable('FIELDS');

  /** @internal */
  readonly TEMPLATE = this.localizationService.getStringLaserficheObservable('TEMPLATE');

  /** @internal */
  SELECTED_TEMPLATE_NAME = this.getSelectedTemplateName();

  /** @internal */
  getSelectedTemplateName(): Observable<string> {
    const templateSelected = this.templateContainer?.templateSelected;
    if (templateSelected) {
      return of(templateSelected.displayName);
    } else {
      return this.templateContainer?.emptyTemplateName;
    }
  }

  @Input()
  initAsync = async (
    lfFieldContainerService: LfFieldContainerService,
    templateIdentifier?: number | string
  ): Promise<void> => {
    this.ref.detectChanges();
    this.lfFieldContainerService = CoreUtils.validateDefined(lfFieldContainerService, 'lfFieldContainerService');
    await this.adhocContainer.initAsync(this.lfFieldContainerService);
    await this.templateContainer.initAsync(
      { templateFieldContainerService: this.lfFieldContainerService },
      templateIdentifier
    );
    this.SELECTED_TEMPLATE_NAME = this.getSelectedTemplateName();
    this.ref.markForCheck();
  };

  @Input()
  forceValidation = (): boolean => {
    const templateIsValid: boolean = this.templateContainer.forceValidation();
    const adhocIsValid: boolean = this.adhocContainer.forceValidation();
    return templateIsValid && adhocIsValid;
  };

  @Input()
  clearAsync = async (): Promise<void> => {
    await this.templateContainer.clearAsync();
    await this.adhocContainer.clearAsync();
  };

  @Input()
  getFieldValues = (): { [fieldName: string]: FieldValue } => {
    const adhocFieldValues: { [fieldName: string]: FieldValue } = this.adhocContainer.getFieldValues();
    const templateFieldValues: { [fieldName: string]: FieldValue } =
      this.templateContainer.getTemplateValue()?.fieldValues ?? {};
    return { ...adhocFieldValues, ...templateFieldValues };
  };

  @Input()
  getNumAdhocFields = (): number => {
    const adhocFields = Object.keys(this.adhocContainer.getFieldValues() ?? {}).length;
    return adhocFields ?? 0;
  };

  @Input()
  resetFieldDataAsync = async (fields: { value: FieldValue; definition: LfFieldInfo }[]): Promise<void> => {
    await this.adhocContainer.resetFieldDataAsync(fields);
    this.ref.markForCheck();
  };

  @Input()
  updateFieldValuesAsync = async (values: FieldValue[]): Promise<void> => {
    await this.adhocContainer.updateFieldValuesAsync(values);
    this.ref.markForCheck();
  };

  @Input()
  getTemplateValue = (): TemplateValue | undefined => {
    return this.templateContainer.getTemplateValue();
  };

  /** @internal */
  onFieldValuesChanged(): void {
    const templateIsValid: boolean = this.templateContainer.isValid();
    const adhocIsValid: boolean = this.adhocContainer.isValid();
    this.fieldValuesChanged.emit(templateIsValid && adhocIsValid);
  }

  /** @internal */
  onTemplateSelectedChange(selectedTemplateId: number) {
    this.templateSelectedChanged.emit(selectedTemplateId);
    this.SELECTED_TEMPLATE_NAME = this.getSelectedTemplateName();
  }

  /** @internal */
  onDialogOpened() {
    this.dialogOpened.emit();
  }

  /** @internal */
  onDialogClosed() {
    this.dialogClosed.emit();
  }
}
